import Configs from '../../../../configs/Configs';
import Session from '../../session/Session';
import Selector from '../selector/Selector';
import Icon from './icon/Icon';
import Progress from './progress/Progress';
import { SKILLS } from '../../../../configs/Configs';

const STATES = {
    READY: 'READY',
    WORKING: 'WORKING',
    CHARGING: 'CHARGING',
} as const;

type EnrageState = keyof typeof STATES;

const EVENTS = {
    HIT: 'enrage_hit',
    CHARGE_START: 'enrage_charge_start',
    CHARGE_END: 'enrage_charge_end',
} as const;

export default class Enrage extends Phaser.GameObjects.Container {
    private heroId = 1;
    private enrageState: EnrageState = 'READY';
    private canStart = false;

    private timerWorking: Phaser.Time.TimerEvent | null = null;
    private timerCharging: Phaser.Time.TimerEvent | null = null;

    private icon!: Icon;
    private progress!: Progress;

    constructor(scene: Phaser.Scene) {
        super(scene, 0, 0);

        this.heroId = 1;

        this.enrageState = STATES.READY;
        this.canStart = false;

        this.timerWorking = null;
        this.timerCharging = null;

        this.setupIcon();
        this.setupProgress();
        this.setupListeners();

        this.setReady();
    }

    static get EVENTS() {
        return EVENTS;
    }

    setupIcon() {
        let x = this.scene.cameras.main.width - 100;
        let y = this.scene.cameras.main.height - 376;

        this.icon = new Icon(this.scene, x, y);
        this.add(this.icon);

        this.icon.on('click', this.start, this);
    }

    setupProgress() {
        let x = this.icon.x;
        let y = this.icon.y + 55;

        this.progress = new Progress(this.scene, x, y);
        this.add(this.progress);
    }

    setupListeners() {
        this.scene.events.on(Selector.EVENTS.HERO_SELECTED, this.onHeroSelected, this);
        this.scene.events.on(Session.EVENTS.ENRAGE_REFRESH_USED, this.onEnrageRefreshUsed, this);
    }

    start() {
        if (this.enrageState !== STATES.READY || !this.canStart) return;
        this.enrageState = STATES.WORKING;

        let skill = SKILLS.BOB.ENRAGE_TIME;
        let level = Session.Instance.getSkillLevel(skill)!;
        let time = Configs.getBobSkill(skill, level).value!;
        let attackSpeed = Configs.getHeroAttackSpeed(this.heroId);

        this.scene.events.emit(EVENTS.HIT);
        this.timerWorking = this.scene.time.addEvent({
            delay: attackSpeed * 1000,
            repeat: Math.floor(time / attackSpeed) - 2,
            callback: this.onTick,
            callbackScope: this,
        });

        this.icon.startWorking();
        this.progress.startWorking(time);
    }

    charge() {
        this.scene.events.emit(EVENTS.CHARGE_START);

        let time = Configs.enrageRefreshTime;

        this.enrageState = STATES.CHARGING;
        this.icon.setCharging();
        this.progress.startCharging(time);

        this.timerCharging = this.scene.time.addEvent({
            delay: time * 1000,
            callback: this.setReady,
            callbackScope: this,
        });
    }

    onTick() {
        this.scene.events.emit(EVENTS.HIT);
        if (this.timerWorking!.repeatCount === 0) this.charge();
    }

    onHeroSelected(heroId: number) {
        if (heroId === 0) {
            this.canStart = false;
            this.setAlpha(0.5);
        } else {
            this.canStart = true;
            this.setAlpha(1);
        }
    }

    onEnrageRefreshUsed() {
        this.setReady();
    }

    setReady() {
        this.timerCharging?.remove();

        this.scene.events.emit(EVENTS.CHARGE_END);
        this.enrageState = STATES.READY;
        this.icon.setReady();
        this.progress.setReady();
    }

    destroy() {
        this.timerWorking?.remove();
        this.timerCharging?.remove();
        this.scene.events.off(Selector.EVENTS.HERO_SELECTED, this.onHeroSelected, this);
        this.scene.events.off(Session.EVENTS.ENRAGE_REFRESH_USED, this.onEnrageRefreshUsed, this);
        super.destroy();
    }
}
